import { QrCode } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { useTranslation } from "react-i18next"

import { InviteCopyButton } from "@/components/invites/invite-copy-button"
import { InviteQrDownloadButton } from "@/components/invites/invite-qr-download-button"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

/**
 * OKS-685 — "Show QR" affordance for the active candidate invite.
 *
 * Renders a trigger button in the `ActiveInvite` action row that opens
 * a shadcn `Dialog` showing a scannable QR of the invite's short URL
 * (`invite.url`, the OKS-655 public `/invite/<CODE>` link). The in-
 * person use case: a brand/agency op holds up their screen at an event
 * / shoot so a co-located creator scans-to-join, no URL dictation.
 *
 * **Client-side generation (TRD Key Decision).** The QR is drawn in the
 * browser via `qrcode.react`'s `QRCodeSVG` — a zero-runtime-dependency,
 * Radix-free lib that emits an inline `<svg>` (crisp at any scan size,
 * DOM-assertable, no canvas taint). The value encoded is already on the
 * client, so there is NO backend surface: no new route, controller,
 * policy, or Inertia prop. Generation is synchronous → no loading/error
 * state (UX Spec §3's Skeleton/error branches were the server-rendered
 * contingency and don't apply).
 *
 * **Fails closed identically to the link.** The QR only encodes the URL
 * string; a revoked/expired invite's QR resolves through the same
 * `/invite/<CODE>` → generic invalid landing. No special handling. The
 * trigger anyway only mounts inside the `invite !== null` branch of
 * `InvitesPanel`, so it never appears on the empty/pre-approval state.
 *
 * **Dark-mode scannability.** The QR plate is a HARDCODED `bg-white`
 * with black modules (`fgColor #000000` / `bgColor #ffffff`) regardless
 * of theme — a themed dark plate won't scan. This deliberately bypasses
 * theme tokens (UX Spec §3 hard requirement).
 *
 * **A11y.** The trigger has a descriptive label (not icon-only), the
 * svg carries a `<title>` (via `title`), and the short URL is rendered
 * as visible text beneath the code — so the QR is never the sole means
 * of conveying the link. shadcn `Dialog` handles focus trap + focus
 * return to the trigger on close.
 */

interface InviteQrButtonProps {
  /** The public short invite URL to encode (`invite.url`). */
  url: string
  /** Button variant — defaults to "outline" (secondary action). */
  variant?: "default" | "outline" | "secondary" | "ghost"
  /** Tailwind className override for the trigger button. */
  className?: string
  /** Override trigger testid. Defaults to `invite-qr-trigger`. */
  testId?: string
}

export function InviteQrButton({
  url,
  variant = "outline",
  className,
  testId = "invite-qr-trigger",
}: InviteQrButtonProps) {
  const { t } = useTranslation()

  return (
    <Dialog>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant={variant}
            data-testid={testId}
            aria-label={t("invites.panel.actions.show_qr_aria")}
            className={cn(className)}
          >
            <QrCode className="size-4" aria-hidden="true" />
            {t("invites.panel.actions.show_qr")}
          </Button>
        }
      />
      <DialogContent data-testid="invite-qr-dialog" className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("invites.panel.qr.title")}</DialogTitle>
          <DialogDescription>
            {t("invites.panel.qr.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          {/* Hardcoded white plate + black modules — MUST stay
              theme-independent so the code scans in dark mode. */}
          <div
            data-testid="invite-qr-image"
            className="flex justify-center rounded-lg bg-white p-4"
          >
            <QRCodeSVG
              value={url}
              size={256}
              level="M"
              marginSize={2}
              bgColor="#ffffff"
              fgColor="#000000"
              title={t("invites.panel.qr.image_alt")}
              className="h-auto w-full max-w-[200px] sm:max-w-[256px]"
            />
          </div>

          {/* Text fallback: the short URL, readable + copyable, so the
              QR is not the only channel for the link. */}
          <div className="flex w-full flex-col items-center gap-2">
            <span
              data-testid="invite-qr-url"
              className="text-muted-foreground text-center font-mono text-xs break-all"
            >
              {url}
            </span>
            <InviteCopyButton
              url={url}
              variant="secondary"
              testId="invites-panel-qr-copy-button"
            />
            {/* OKS-690 — save the QR as a print-ready PNG. Sits after
                copy (reading order title → QR → url → copy → download)
                as a secondary peer, never the dialog's primary action. */}
            <InviteQrDownloadButton url={url} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
